import React from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Tooltip,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
  Sync as SyncIcon,
} from '@material-ui/icons'
import { colors } from '../../../../Themes/Colors'
import {
  useGetAdminOneOnOneCoachingEventsQuery,
  useRefetchGongTranscriptMutation,
  useRefetchHubspotMutation,
} from '../../../../Generated/graphql-types'
import {
  PageContainer,
  HeaderSection,
  BackButton,
  TitleSection,
  Title,
  Subtitle,
  StatusChip,
  SectionTitle,
  InfoCard,
  InfoGrid,
  InfoItem,
  InfoLabel,
  InfoValue,
  LoadingContainer,
  ErrorContainer,
  InfoValueLong,
} from './OneOnOneCoachingDetail.styled'

interface RouteParams {
  id: string
}

export const OneOnOneCoachingDetail: React.FC = () => {
  const { id } = useParams<RouteParams>()
  const history = useHistory()
  const eventId = parseInt(id, 10)

  const { data, loading, error, refetch } =
    useGetAdminOneOnOneCoachingEventsQuery({
      variables: { id: eventId },
      skip: !eventId,
    })

  const [
    refetchHubspotMutation,
    { loading: refetchHSLoading, error: refetchHSError },
  ] = useRefetchHubspotMutation({
    onCompleted: () => {
      refetch()
    },
  })
  const [
    refetchGongTranscriptMutation,
    { loading: refetchGongLoading, error: refetchGongError },
  ] = useRefetchGongTranscriptMutation({
    onCompleted: () => {
      refetch()
    },
  })

  const event = data?.adminOneOnOneCoachingEvents?.events[0]

  const handleBack = () => {
    history.push('/internal/self-serve/one-on-one-coaching')
  }

  const handleHubspotButtonClick = async () => {
    if (!event) {
      return
    }
    await refetchHubspotMutation({ variables: { id: event.id } })
    if (refetchHSError) {
      console.error(refetchHSError)
    }
  }

  const handleGongButtonClick = async () => {
    if (!event) {
      return
    }
    await refetchGongTranscriptMutation({ variables: { id: event.id } })
    if (refetchGongError) {
      console.error(refetchGongError)
    }
  }

  if (error) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to 1:1 Coaching
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            Error Loading Event
          </Typography>
          <Typography>{error.message}</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  if (loading) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to 1:1 Coaching
        </BackButton>
        <LoadingContainer>
          <CircularProgress />
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (!event) {
    return (
      <PageContainer maxWidth='lg'>
        <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back to 1:1 Coaching
        </BackButton>
        <ErrorContainer>
          <Typography variant='h5' gutterBottom>
            Event Not Found
          </Typography>
          <Typography>Event with ID {eventId} not found</Typography>
        </ErrorContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth='lg'>
      <HeaderSection>
        <BackButton
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          variant='text'
        >
          Back to 1:1 Coaching
        </BackButton>

        <TitleSection>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='flex-start'
            width='100%'
          >
            <Box flex={1}>
              <Box display='flex' alignItems='center'>
                <Title>{event.meetingTitle || 'Untitled Event'}</Title>
                {event.status ? (
                  <StatusChip
                    label={event.status}
                    status={event.status}
                    size='small'
                  />
                ) : null}
              </Box>
              <Subtitle>Event ID: {event.id}</Subtitle>
            </Box>
            <Box>
              {event.hubspotId ? (
                <Tooltip title='Sync Hubspot'>
                  <Button
                    variant='contained'
                    size='small'
                    startIcon={<SyncIcon />}
                    onClick={handleHubspotButtonClick}
                    style={{
                      backgroundColor: colors.resiliaBlue,
                      color: 'white',
                      textTransform: 'none',
                    }}
                    disabled={refetchHSLoading}
                  >
                    Sync Hubspot
                  </Button>
                </Tooltip>
              ) : null}{' '}
              {event.videoConferenceUrl ? (
                <Tooltip title='Sync Gong'>
                  <Button
                    variant='contained'
                    size='small'
                    startIcon={<SyncIcon />}
                    onClick={handleGongButtonClick}
                    style={{
                      backgroundColor: colors.resiliaBlue,
                      color: 'white',
                      textTransform: 'none',
                    }}
                    disabled={refetchGongLoading}
                  >
                    Sync Gong
                  </Button>
                </Tooltip>
              ) : null}
            </Box>
          </Box>
        </TitleSection>
      </HeaderSection>

      {/* Basic Information */}
      <InfoCard>
        <CardContent>
          <SectionTitle>
            <InfoIcon />
            Basic Information
          </SectionTitle>
          <InfoGrid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <InfoItem>
                <InfoLabel>Start Date</InfoLabel>
                <InfoValue>
                  {event.startsAt
                    ? new Date(event.startsAt).toLocaleString()
                    : 'N/A'}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoItem>
                <InfoLabel>End Date</InfoLabel>
                <InfoValue>
                  {event.endsAt
                    ? new Date(event.endsAt).toLocaleString()
                    : 'N/A'}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoItem>
                <InfoLabel>Created Date</InfoLabel>
                <InfoValue>
                  {event.createdAt
                    ? new Date(event.createdAt).toLocaleString()
                    : 'N/A'}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InfoItem>
                <InfoLabel>Deleted Date</InfoLabel>
                <InfoValue>
                  {event.deletedAt
                    ? new Date(event.deletedAt).toLocaleString()
                    : 'N/A'}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Contact</InfoLabel>
                <InfoValue>
                  {event.contactName || 'N/A'}
                  <br />
                  {event.contactEmail || ''}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Coach</InfoLabel>
                <InfoValue>
                  {event.organizerName || 'N/A'}
                  <br />
                  {event.organizerEmail || ''}
                </InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Event Type</InfoLabel>
                <InfoValue>{event.meetingType || 'N/A'}</InfoValue>
              </InfoItem>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Hubspot ID</InfoLabel>
                <InfoValue>{event.hubspotId || 'N/A'}</InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={8} md={8}>
              <InfoItem>
                <InfoLabel>Video Conference URL</InfoLabel>
                <InfoValue>{event.videoConferenceUrl || 'N/A'}</InfoValue>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Meeting Body</InfoLabel>
                <InfoValueLong>{event.meetingBody || 'N/A'}</InfoValueLong>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Summary</InfoLabel>
                <InfoValueLong>{event.summary || 'N/A'}</InfoValueLong>
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <InfoItem>
                <InfoLabel>Transcript</InfoLabel>
                <InfoValueLong>{event.transcript || 'N/A'}</InfoValueLong>
              </InfoItem>
            </Grid>
          </InfoGrid>
        </CardContent>
      </InfoCard>
    </PageContainer>
  )
}
